import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts'
import { useGetApiCompaniesCurrentEmployeesReportingCollectedpoints } from '@/api/generated/company-management-user/company-management-user'
import { FilterRowCharts } from '@/components/common/filters/FilterRowCharts'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import { ChartCard } from '../components/ChartCard'
import type { DashboardDateFilter } from '../dateFilter'
import { formatBucketAxisLabel, formatBucketRange } from './bucketLabels'
import { EURO_FORMAT } from './constants'

interface Props {
  filter: DashboardDateFilter
  isFilterValid: boolean
}

export function CollectedPointsChart({ filter, isFilterValid }: Props) {
  const { t } = useTranslation('dashboard')

  const { data, isFetching, isError } = useGetApiCompaniesCurrentEmployeesReportingCollectedpoints(filter, {
    query: { enabled: isFilterValid },
  })

  const buckets = data?.data?.buckets ?? []
  const chartData = buckets.map((bucket) => ({
    name: formatBucketAxisLabel(bucket.startDate, filter.bucketSize),
    range: formatBucketRange(bucket.startDate, bucket.endDate),
    value: bucket.totalPoints,
    average: bucket.averagePointsPerEmployee,
    euro: bucket.euroValue,
  }))
  const totalEuro = buckets.reduce((sum, bucket) => sum + bucket.euroValue, 0)

  function chart() {
    return (
      <LoaderGeneral isLoading={isFetching} isError={isError} isEmpty={!isFilterValid || chartData.length === 0} className="h-[260px]">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--muted-foreground)" strokeOpacity={0.25} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--foreground)' }} tickLine={false} axisLine={false} minTickGap={16} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: 'var(--muted)' }}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="rounded-lg border bg-card px-3 py-2 shadow-md text-sm">
                    <p className="font-semibold text-card-foreground mb-0.5">{payload[0].payload.range}</p>
                    <p className="text-primary">{t('charts.collectedPoints.tooltipPoints')}: <span className="font-bold">{payload[0].payload.value.toLocaleString('de-DE')}</span></p>
                    <p className="text-muted-foreground">{t('charts.collectedPoints.tooltipAverage')}: {Math.round(payload[0].payload.average).toLocaleString('de-DE')}</p>
                    <p className="text-muted-foreground">{t('charts.collectedPoints.tooltipEuro')}: {EURO_FORMAT.format(payload[0].payload.euro)}</p>
                  </div>
                ) : null
              }
            />
            <Legend formatter={() => t('charts.collectedPoints.legend')} />
            <Bar dataKey="value" fill="var(--chart-1)" radius={[4, 4, 0, 0]} maxBarSize={36} />
          </BarChart>
        </ResponsiveContainer>
      </LoaderGeneral>
    )
  }

  function controls() {
    return (
      <FilterRowCharts>
        <span className="text-sm text-muted-foreground">{t('charts.collectedPoints.totalEuro', { value: EURO_FORMAT.format(totalEuro) })}</span>
      </FilterRowCharts>
    )
  }

  return (
    <ChartCard
      title={t('charts.collectedPoints.title')}
      chart={chart()}
      controls={controls()}
    />
  )
}
